/**
 * Route → Feature key map for sidebar filtering.
 *
 * Each sidebar entry maps to (at most) one feature key from the catalog in
 * routes_admin_permissions.py. If the caller's `effective` feature set on
 * their currently-selected community does NOT include the mapped key, the
 * sidebar entry is hidden.
 *
 * Routes not listed here are always shown (dashboards, settings, etc.).
 * Only feature-gated destinations are filtered.
 */

// Keys must match routes_admin_permissions.FEATURE_CATALOG entries exactly.
export const ROUTE_TO_FEATURE = {
  // Financials
  "/admin/finance": "finance.dashboard",
  "/admin/coa": "finance.coa",
  "/admin/gl": "finance.gl",
  "/admin/journal-entries": "finance.journal",
  "/admin/leases": "finance.rent",
  "/admin/bank-recon": "finance.bank",
  "/admin/trust-accounts": "finance.trust",
  "/admin/ap": "finance.ap",
  "/admin/payments": "finance.payments",
  "/admin/delinquency": "finance.delinquency",
  "/admin/delinquency-waterfall": "finance.delinquency",
  "/admin/reserve-study": "finance.reserve",
  "/admin/estoppel": "finance.reserve",
  "/admin/dues": "finance.rent",
  "/admin/tax/1099": "finance.tax",
  "/admin/tax/1099-misc": "finance.tax",

  // Community
  "/admin/users": "community.users",
  "/admin/residents": "community.residents",
  "/admin/committees": "community.committees",

  // Operations
  "/admin/maintenance": "ops.maintenance",
  "/admin/pm-schedule": "ops.pm_schedule",
  "/admin/inspections": "ops.inspections",
  "/admin/unit-turnover": "ops.turnover",
  "/admin/move-in-checklist": "ops.turnover",
  "/admin/move-out-wizard": "ops.turnover",
  "/admin/packages": "ops.packages",
  "/admin/access": "ops.access",
  "/admin/emergency": "ops.emergency",
  "/admin/parking": "ops.parking",
  "/admin/pets": "ops.pets",

  // Leasing
  "/admin/leasing": "leasing.listings",
  "/admin/screening": "leasing.screening",

  // Governance
  "/admin/board-meetings": "gov.board",
  "/admin/board-terms": "gov.board",
  "/admin/voting": "gov.board",
  "/admin/arc": "gov.arc",
  "/admin/violations": "gov.violations",
  "/admin/documents": "gov.documents",

  // Vendors
  "/admin/vendors": "vendors.directory",
  "/admin/vendor-compliance": "vendors.compliance",
  "/admin/vendor-scorecards": "vendors.compliance",

  // AI & Tools
  "/admin/ai/concierge": "ai.concierge",
  "/admin/approvals": "tools.approvals",
  "/admin/pet-approvals": "tools.approvals",
  "/admin/bulk-import": "tools.bulk",
};

/**
 * Filter a list of sidebar groups so only feature-permitted items remain.
 *
 * @param {Array} groups — [{items: [{to, label, ...}]}]
 * @param {Set<string>} effective — feature keys the caller holds
 * @param {string} role — caller role (admins/PMs bypass filtering)
 * @returns {Array} filtered groups (empty groups dropped)
 */
export function filterGroupsByFeatures(groups, effective, role) {
  // Admin + property_manager always see the whole shell — the per-user
  // grant system is meant to *unlock* extra pages for residents/vendors/
  // board members, not to hide them from staff.
  if (role === "admin" || role === "property_manager") return groups;
  const eff = effective instanceof Set ? effective : new Set(effective || []);
  return groups
    .map((g) => {
      const items = g.items.filter((it) => {
        const key = ROUTE_TO_FEATURE[it.to];
        if (!key) return true;      // no mapping = always visible
        return eff.has(key);
      });
      return { ...g, items };
    })
    .filter((g) => g.items.length > 0);
}
